// @pages/document/FinalEdits.page.tsx

import React, { useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { renderRoute, routeReviewWithDocument } from "@navigation/typedRoutes";
import { useContentClient } from '@contexts/ContentClient.context';
import { useDocumentContext } from '@contexts/Document.context';
import { useLoadingController } from '@contexts/Loading.context';
import type {
    ContentError,
    ReplaceSectionRequest,
    ReplaceSectionResponse,
    Section,
    UserDocument,
} from "@ps-bridge/models/core/dataContracts";
import type { DocumentID } from "@ps-bridge/models/brands/documentID";
import type { SectionID } from "@ps-bridge/models/brands/sectionID";
import { useIoState } from '@utils/useIoState';
import { ok, type Ok } from "@ps-bridge/ok";
import type { OptionTs, TaskEitherTs, TaskTs } from "@ps-bridge/interop";
import type { StateRead, StateUpdate } from "@ps-bridge/capabilitiesTypes";
import type { EmitOnRouteChange } from "@ps-bridge/capabilitiesTypes";
import {
    ioEffect,
    taskEitherFromEffect,
    taskEitherMap,
} from '@utils/effects/branded';
import { useOnInit, useOnRouteChange } from "@hooks/effects/useIoEffect";
import { mkNavigate, mkRouteBuilder, mkTaskRunner } from "@ps-bridge/capabilities";
import type { ReplaceSectionFetch } from "@ps-bridge/contracts/documentFinalEdits";
import type { DocumentFinalEditsKernel } from "@ps-bridge/contracts/documentFinalEdits";
import { buildDocumentFinalEditsViewProps } from "@ps-bridge/documentFinalEdits.program";
import { useRunTaskWithError } from "@hooks/effects/useTaskEffect";
import * as Option from "@utils/Option";
import { optionToPsMaybe } from "@src/purescript/interop";
import FinalEditsPagePanel, {
    type FinalEditsPagePhase,
} from "@view/panels/document/FinalEditsPage.panel";
import { getParamOption } from "@utils/searchParams";
import { makeSectionID } from "@ps-bridge/models/brands/sectionID";

const toPsMaybe = <A,>(value: OptionTs<A>): OptionTs<A> => optionToPsMaybe(value);

const normalizeSectionForKernel = (section: Section): Section => ({
    ...section,
    metadata: {
        ...section.metadata,
        last_edited: toPsMaybe(section.metadata.last_edited),
    },
    section_contents: toPsMaybe(section.section_contents),
});

const normalizeDocumentForKernel = (document: UserDocument): UserDocument => ({
    ...document,
    document_contents: document.document_contents.map(normalizeSectionForKernel),
});

const normalizeReplaceSectionResponse = (response: ReplaceSectionResponse): ReplaceSectionResponse => ({
    ...response,
    updated_section: normalizeSectionForKernel(response.updated_section),
});

const FinalEditsPage: React.FC = () => {
    const navigate = useNavigate();
    const contentClient = useContentClient();
    const { document, activeSection, controller } = useDocumentContext();
    const [searchParams] = useSearchParams();
    const documentOption = document.read;
    const { read: finalEdits, set: setFinalEdits } = useIoState('');

    const loading = useLoadingController();
    const routeSectionId: OptionTs<SectionID> = useMemo(
        () =>
            Option.fold(
                getParamOption(searchParams, "section_id"),
                () => Option.none(),
                (value) => Option.some(makeSectionID(value)),
            ),
        [searchParams],
    );
    const syncRouteSectionOnRouteChange = useMemo(
        () =>
            ((nextRouteSectionId: OptionTs<SectionID>) =>
                ioEffect(() => {
                    Option.fold(nextRouteSectionId, () => ok, (sectionId) => {
                        Option.fold(
                            activeSection.read,
                            () => activeSection.setSome(sectionId),
                            (value) => (value === sectionId ? ok : activeSection.setSome(sectionId)),
                        );
                        return ok;
                    });
                    return ok;
                })) as EmitOnRouteChange<OptionTs<SectionID>>,
        [activeSection],
    );
    useOnRouteChange(routeSectionId, syncRouteSectionOnRouteChange);
    const runTaskWithError = useRunTaskWithError();
    const runTaskWithErrorIo = useCallback(
        (task: TaskTs<Ok>) =>
            ioEffect(() => {
                runTaskWithError(task).then(() => ok);
                return ok;
            }),
        [runTaskWithError],
    );
    const taskRunner = useMemo(() => mkTaskRunner(runTaskWithErrorIo), [runTaskWithErrorIo]);
    const replaceSectionFetch: ReplaceSectionFetch = useMemo(() => {
        const invoke = (
            documentId: DocumentID,
            request: ReplaceSectionRequest,
        ): TaskEitherTs<ContentError, ReplaceSectionResponse> =>
            taskEitherMap(
                taskEitherFromEffect(
                    contentClient.replaceSection(documentId, request),
                    (reason) => reason as ContentError,
                ),
                normalizeReplaceSectionResponse,
            );

        function fetch(documentId: DocumentID, request: ReplaceSectionRequest): ReturnType<typeof invoke>;
        function fetch(documentId: DocumentID): (request: ReplaceSectionRequest) => ReturnType<typeof invoke>;
        function fetch(documentId: DocumentID, request?: ReplaceSectionRequest) {
            if (request === undefined) {
                return (nextRequest: ReplaceSectionRequest) => invoke(documentId, nextRequest);
            }
            return invoke(documentId, request);
        }

        return Object.assign(fetch, {
            __brandReplaceSectionFetch: "ReplaceSectionFetch" as const,
        }) as ReplaceSectionFetch;
    }, [contentClient]);
    const documentKernelRead = useMemo(
        () => Option.map(normalizeDocumentForKernel)(document.read) as StateRead<OptionTs<UserDocument>>,
        [document.read],
    );
    const documentKernelHandle = useMemo(
        () => ({
            read: documentKernelRead,
            setSome: document.setSome,
            setMaybe: document.setMaybe,
        }),
        [documentKernelRead, document.setMaybe, document.setSome],
    );
    const activeSectionKernelHandle = useMemo(
        () => ({
            read: activeSection.read,
            setSome: activeSection.setSome,
            setMaybe: activeSection.setMaybe,
        }),
        [activeSection.read, activeSection.setMaybe, activeSection.setSome],
    );
    const kernel: DocumentFinalEditsKernel = useMemo(
        () => ({
            loading,
            navigate: mkNavigate((route) =>
                ioEffect(() => {
                    navigate(renderRoute(route), { replace: true });
                    return ok;
                }),
            ),
            reviewRoute: mkRouteBuilder(routeReviewWithDocument),
            document: documentKernelHandle,
            activeSection: activeSectionKernelHandle,
            finalEdits: {
                read: finalEdits,
                set: setFinalEdits,
            },
            updateSection: controller.updateSection as StateUpdate<Section>,
            replaceSection: replaceSectionFetch,
            taskRunner,
        }),
        [
            activeSectionKernelHandle,
            controller.updateSection,
            documentKernelHandle,
            finalEdits,
            loading,
            navigate,
            replaceSectionFetch,
            setFinalEdits,
            taskRunner,
        ],
    );
    const viewProps = useMemo(
        () =>
            buildDocumentFinalEditsViewProps({
                kernel,
            }),
        [kernel],
    );

    useOnInit(viewProps.onInit);

    const phase: FinalEditsPagePhase = Option.fold(
        documentOption,
        () => "missing",
        () => "ready",
    );

    return <FinalEditsPagePanel phase={phase} viewProps={viewProps} />;
};

export default FinalEditsPage;
